<template>
  <footer
    v-if="minimal.loaded"
    class="ns-footer"
    :class="{ rtl: dir === 'rtl', 'ns-minimal-footer': minimal.active }"
  >
    <section
      class="footer-top"
      :class="{ 'show-my-site-background-color': useMysiteFooter }"
      :hidden="noExpandedFooter"
    >
      <slot name="logo-container">
        <NsFooterStorefront
          v-if="useMysiteFooter"
          :storefront="footer.storefront"
        />
        <div v-else class="logo-container">
          <div class="ns-container">
            <slot v-if="!hideLogo" name="logo">
              <NsLogo
                :is-white="!minimal.active"
                :is-word-mark="true"
                alt-text="Nu Skin Logo on the Footer"
                link-url=""
              />
            </slot>

            <div
              v-if="!hideJoinButton && showJoinButton"
              class="logo-container-button"
            >
              <slot name="join-button">
                <a
                  class="inverse-ghost join-button"
                  :href="getFullyQualifiedUrl(footer.joinNuskinUrl)"
                  @click="joinButtonClick"
                >
                  {{ footer.joinNuskin }}
                </a>
              </slot>
            </div>
          </div>
        </div>
      </slot>
      <slot v-if="!useMysiteFooter" name="navigation-container">
        <div class="navigation-container">
          <div class="ns-container">
            <slot name="footer-nav">
              <NsFooterNav
                :navigation="footer.navigation"
                :expandbreakpoint="expandbreakpoint"
                :minimal="minimal.active"
              />
            </slot>
            <div class="social-icons">
              <slot name="social-icons">
                <NsSocialIcons
                  v-if="showSocialIcons"
                  :social-media-header-text="footer.socialMediaHeaderText"
                  :social-media-list="footer.socialMediaList"
                  :stacked="$mq !== 'tablet'"
                  :minimal="minimal.active"
                />
              </slot>
            </div>
          </div>
        </div>
      </slot>
    </section>
    <section
      v-if="
        footer.detailsLinks.length > 0 || footer.enablegdprcookiepreferences
      "
      class="footer-bottom"
    >
      <div class="max-crop">
        <slot name="bottom-links">
          <div
            v-for="detailLink in footer.detailsLinks"
            :key="detailLink.name"
            class="bottom-link"
          >
            <a
              :href="getFullyQualifiedUrl(detailLink.href)"
              class="base"
              target="_self"
              v-html="detailLink.name"
            />
          </div>

          <slot name="gdpr-section">
            <div v-if="footer.enablegdprcookiepreferences" class="bottom-link">
              <span id="teconsent"></span>
            </div>
          </slot>
        </slot>
      </div>
      <div
        v-if="footer.seals.length > 0 && minimal.active"
        class="seals-container seals-container-minimal"
      >
        <div class="max-crop">
          <div v-for="seal in footer.seals" :key="seal.title" class="seal">
            <a v-if="hasValidLink(seal)" :href="seal.sealHref" target="_blank">
              <img
                :src="getFullyQualifiedUrl(seal.sealImageUrl)"
                :alt="seal.title"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
    <slot v-if="!useMysiteFooter" name="seal-icons">
      <div
        v-if="footer.seals.length > 0"
        class="seals-container seals-container-full"
      >
        <div class="max-crop">
          <div v-for="seal in footer.seals" :key="seal.title" class="seal">
            <a v-if="hasValidLink(seal)" :href="seal.sealHref" target="_blank">
              <img
                :src="getFullyQualifiedUrl(seal.sealImageUrl)"
                :alt="seal.title"
              />
            </a>
          </div>
        </div>
      </div>
    </slot>
  </footer>
</template>

<script>
import logo from "../assets/logo-white.svg";
import { AccountManager } from "@nuskin/ns-account";
import { DirUtil, events } from "@nuskin/ns-util";
import { FooterService } from "@nuskin/ns-product";
import { fromJsonString, getFullUrl } from "@nuskin/ns-common-lib";
import { getConfiguration } from "@nuskin/configuration-sdk";
import "@nuskin/ns-core-styles/src/dist/main.css";

import NsLogo from "./NsLogo.vue";
import NsFooterStorefront from "./NsFooterStorefront.vue";
import NsSocialIcons from "./NsSocialIcons.vue";
import NsFooterNav from "./NsFooterNav.vue";
import vueEventBus from "./EventBus";

export default {
  name: "NsFooter",
  components: { NsFooterStorefront, NsLogo, NsSocialIcons, NsFooterNav },
  props: {
    /**
     * Which country to display
     */
    currentDisplayCountry: {
      type: String,
      default: ""
    },
    /**
     * The country code of the currently displayed country
     */
    currentCountryCode: {
      type: String,
      default: ""
    },
    /**
     * Which language to display
     */
    currentDisplayLanguage: {
      type: String,
      default: ""
    },
    /**
     * The language code of the currently displayed country
     */
    currentLanguageCode: {
      type: String,
      default: ""
    },
    /**
     * Links that go between navigation and seals for legal type stuff
     */
    detailsLinks: {
      type: [String, Array],
      default: null
    },
    /**
     * Will place in anchor HTML for trust arc cookie link
     */
    enablegdprcookiepreferences: Boolean,
    /**
     * Used to lock breakpoints into a certain mode such as desktop, tablet, or mobile (deprecated)
     */
    expandbreakpoint: {
      type: String,
      default: "none"
    },
    /**
     * Option to hide the country/language dropdown
     */
    hideCountryLanguageDropdown: Boolean,
    /**
     * Option to hide the join button
     */
    hideJoinButton: Boolean,
    /**
     * Option to hide the logo section
     */
    hideLogo: Boolean,
    /**
     * Option to hide the social icons section
     */
    hideSocial: Boolean,
    /**
     * text for the button to join NuSkin
     */
    joinNuskin: {
      type: String,
      default: ""
    },
    /**
     * Where the join button should take the user
     */
    joinNuskinUrl: {
      type: String,
      default: ""
    },
    /**
     * The text for the country/language dropdown
     */
    langButtonText: {
      type: String,
      default: ""
    },
    /**
     * Where to redirect the user when they click the logo
     */
    logoURL: {
      type: String,
      default: logo
    },
    /**
     * Used to determine if the mysite/storefront footer style should be used.
     */
    mysite: Boolean,
    /**
     * Used to force the component to ignore My Site footer data.
     */
    noMysite: Boolean,
    /**
     * An array of navigation objects. Each object should contain: title, links,  addedContent (optional)
     */
    navigation: {
      type: [String, Array],
      default: null
    },
    /**
     * Will hide the top part of the footer. Use if you want a simple footer.
     */
    noExpandedFooter: Boolean,
    /**
     * Pull data from the AEM footer service if not sent in attributes
     */
    noFooterService: Boolean,
    /**
     * An array of objects for seal information. Each object should contain: title, sealImageUrl
     */
    seals: {
      type: [String, Array],
      default: null
    },
    /**
     * Title text for the social icons section
     */
    socialMediaHeaderText: {
      type: String,
      default: ""
    },
    /**
     * An array of social media items. Each object should contian: unknown
     */
    socialMediaList: {
      type: [String, Array],
      default: null
    },
    /**
     * Object of storefront values you can pass in directly instead of using service
     */
    storefront: {
      type: Object,
      default: null
    },
    /**
     * (DEPECRECATED) Used to style footer in storefront.
     * WARNING: Please use the mysite prop instead, to be consistent with NsHeader component.
     */
    storefrontFooter: Boolean,
    /**
     * Analytics id for click event
     */
    analyticsId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      footerData: {},
      minimal: {
        value: false,
        loaded: false
      }
    };
  },
  computed: {
    market() {
      return this.$NsAppService.runConfig.market || "";
    },
    language() {
      return this.$NsAppService.runConfig.language || "";
    },
    siteUrl() {
      return this.$NsAppService.siteUrl || "";
    },
    isStorefront() {
      return !!this.$NsAppService.isStorefront;
    },
    useMysiteFooter() {
      return (
        !this.noMysite &&
        (this.isStorefront || this.mysite || this.storefrontFooter)
      );
    },
    dir() {
      return DirUtil.getDir();
    },
    showJoinButton() {
      return !AccountManager.isLoggedIn();
    },
    // noFooterService must be true if you don't want to pull default data from AEM
    footer() {
      let navigation = this.useMysiteFooter
        ? (this.footerData.storefront || {}).navigation
        : this.footerData.navigation;
      return {
        currentCountryCode:
          this.currentCountryCode ||
          this.market ||
          this.footerData.currentCountryCode ||
          "US",
        currentDisplayCountry:
          this.currentDisplayCountry ||
          this.footerData.currentDisplayCountry ||
          "United States",
        currentDisplayLanguage:
          this.currentDisplayLanguage ||
          this.footerData.currentDisplayLanguage ||
          "English",
        currentLanguageCode:
          this.currentLanguageCode ||
          this.language ||
          this.footerData.currentLanguageCode ||
          "en",
        detailsLinks: this.useMysiteFooter
          ? (this.footerData.storefront || {}).detailsLinks || []
          : this.j(this.detailsLinks) || this.footerData.detailsLinks || [],
        seals: this.j(this.seals) ||
          this.footerData.seals || [
            {
              title: "Direct Selling Association",
              sealImageUrl: "/content/dam/apps/ns_footer_assets/DSA.png"
            },
            {
              title: "Better Business Bureau",
              sealImageUrl: "/content/dam/apps/ns_footer_assets/BBB.png"
            },
            {
              title: "Direct Selling Education Fund",
              sealImageUrl: "/content/dam/apps/ns_footer_assets/DSEF.png"
            }
          ],
        enablegdprcookiepreferences:
          this.enablegdprcookiepreferences ||
          this.footerData.enableGDPR ||
          false,
        hideLanguageCountryDropdown:
          this.hideLanguageCountryDropdown ||
          this.footerData.hideLanguageCountryDropdown ||
          true,
        joinNuskin:
          this.joinNuskin || this.footerData.joinNuskinLabel || "Join Nu Skin",
        joinNuskinUrl:
          this.joinNuskinUrl ||
          this.footerData.joinNuskinUrl ||
          "/content/nuskin/en_US/sign-up.html",
        navigation: this.j(this.navigation) || navigation || [],
        overrideLogo:
          this.overrideLogo || this.footerData.overrideLogo || false,
        socialMediaHeaderText:
          this.socialMediaHeaderText ||
          this.footerData.socialMediaHeaderText ||
          "CONNECT WITH NU SKIN",
        socialMediaList:
          this.socialMediaList || this.footerData.socialMediaList || [],
        storefront: this.storefront || this.footerData.storefront || {}
      };
    },
    showSocialIcons() {
      return (
        !!this.footer.socialMediaList &&
        this.footer.socialMediaList.length > 0 &&
        !this.hideSocial
      );
    }
  },
  async beforeCreate() {
    try {
      const config = await getConfiguration(["Equinox_Markets"]);
      const active = config.Equinox_Markets.active;

      this.minimal = {
        active: active,
        loaded: true
      };
    } catch (e) {
      this.minimal = {
        loaded: true,
        value: false
      };
    }
  },
  mounted() {
    this.addListeners();
  },
  destroyed() {
    this.removeListeners();
  },
  methods: {
    hasValidLink(item) {
      return item.sealImageUrl && item.sealImageUrl !== "#";
    },

    async getFooterData() {
      if (this.noFooterService) {
        return;
      }

      this.footerData = await FooterService.getFooterData();
    },

    // short alias for fromJsonString
    j(string, defaultValue) {
      return fromJsonString(string, defaultValue);
    },

    joinButtonClick() {
      if (this.analyticsId) {
        events.publish(events.shop.NS_JOIN_NUSKIN_FOOTER_CLICKED, {
          data: this.analyticsId
        });
      }
    },

    getFullyQualifiedUrl(url) {
      return getFullUrl(this.siteUrl, url);
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // watch app service, user service, and feature flag service finished loading
      // listen for run config changes
      events.subscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );

      // watch app service, user service, and feature flag service finished loading
      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.getFooterData,
        {
          immediate: true
        }
      );

      // listen to config changes
      vueEventBus.$on("config-changed", this.getFooterData);
      events.subscribe("updateHeaderLanguage", this.getFooterData);
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();
      events.unsubscribe("updateHeaderLanguage", this.getFooterData);
      events.unsubscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.ns-footer {
  width: 100%;
  padding: 0;
  text-align: start;
  box-sizing: border-box;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  -webkit-font-smoothing: antialiased;
  clear: both;

  * {
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
  }

  .ns-container {
    margin: 0 auto;
    background-color: transparent;
  }

  .footer-top {
    display: flex;
    flex-direction: column;
    color: white;
    background-color: map.get(colors.$themeColors, color-interactive-8);
    margin: auto;

    .logo-container {
      width: 100%;
      max-width: 1440px;
      margin: 0 auto;
      padding: 45px 15px 35px;
      background-color: map.get(colors.$themeColors, color-interactive-9);

      .ns-container {
        display: flex;
        flex-direction: column;
        max-width: 768px;
        margin: 0 auto;
      }

      ::slotted(ns-logo) {
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 20px;
        max-width: 130px;
        display: flex;
      }
    }

    .navigation-container {
      width: 100%;
      padding: 15px 15px 30px;
      max-width: 768px;
      margin: 0 auto;
    }
  }

  .footer-top.show-my-site-background-color {
    background-color: map.get(colors.$themeColors, color-interactive-9);
  }

  a.join-button {
    color: #ffffff;
  }

  .max-crop {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }

  .footer-bottom {
    padding: 20px 15px;
    background-color: white;
    width: 100%;
  }

  .seals-container {
    width: 100%;
    background: map.get(colors.$themeColors, color-gray-6);
    padding: 10px 15px;

    .seal {
      &:not(:first-child) {
        padding-left: 7.5px;
      }

      &:not(:last-child) {
        padding-right: 7.5px;
      }
    }

    a {
      display: inline-block;
    }

    img,
    ::slotted(img) {
      opacity: 0.7;
      height: 50px;
      width: auto;
    }
  }
}

.bottom-link {
  position: relative;
  padding: 0;

  &:not(:first-child) {
    padding-left: 12px;
  }

  &:not(:last-child) {
    padding-right: 12px;
  }
}
.bottom-link a,
.bottom-link ::v-deep a {
  color: map.get(colors.$themeColors, color-interactive-8);
  font-size: 13px;
  line-height: 22px;
  white-space: nowrap;
  font-weight: map.get(
    typographyVariables.$typography,
    font-weight-regular
  ) !important;
  transition: map.get(other.$otherUtils, global-transition) !important;
}
.bottom-link a:hover,
.bottom-link ::v-deep a:hover {
  color: map.get(colors.$themeColors, color-interactive-7);
}
.bottom-link:not(:last-child)::after,
.copyright:not(:last-child)::after {
  content: "";
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 1px;
  height: 10px;
  background-color: map.get(colors.$themeColors, color-interactive-8);
}
.social-icons {
  display: inline-block;
  padding-top: 15px;
}
.logo-container-button {
  margin: 20px auto 0;

  ns-button {
    margin-top: 0;
  }
}

@media (min-width: 768px) {
  .ns-footer {
    .footer-top {
      .logo-container {
        padding: 25px 15px;

        .ns-container {
          flex-direction: row;
          align-items: center;

          .logo-container-button {
            margin: 0 0 0 auto;
          }
        }
      }

      .social-icons {
        padding-top: 40px;
      }
    }

    .seals-container {
      img,
      ::slotted(img) {
        height: 38px;
      }
    }
  }
}

@media (min-width: 1024px) {
  .ns-footer {
    .footer-top {
      .logo-container {
        .ns-container {
          max-width: 100%;
        }
      }

      .navigation-container {
        max-width: 100%;
        padding: 30px 15px;
      }
    }

    .max-crop {
      justify-content: flex-start;
    }
  }

  .ns-footer.ns-minimal-footer {
    .footer-top {
      .logo-container {
        border-bottom: solid 1px black;
      }
    }
  }
}

@media (min-width: 1440px) {
  .ns-footer {
    .footer-top {
      position: relative;
      flex-direction: row-reverse;
      background-color: map.get(colors.$themeColors, color-interactive-8);

      .navigation-container {
        width: auto;
        flex-grow: 1;
        padding: 60px 0;
        padding-left: calc((100% - 1410px) / 2);
      }

      .logo-container {
        display: flex;
        width: auto;
        padding: 60px 0;
        padding-right: calc((100% - 1410px) / 2);
        padding-left: 120px;

        .ns-container {
          margin: 0;
          padding-left: 15px;
          flex-direction: column;
          justify-content: center;
          align-items: center;

          .logo-container-button {
            margin: 10px 0 0;
          }
        }
      }
    }

    .footer-top.show-my-site-background-color {
      background-color: map.get(colors.$themeColors, color-interactive-9);
    }

    .max-crop {
      max-width: 1410px;
      margin: 0 auto;
    }
  }

  .ns-footer.rtl {
    .footer-top {
      .navigation-container {
        &::before {
          background-color: map.get(colors.$themeColors, color-interactive-9);
        }
      }

      .logo-container {
        .ns-container {
          margin-left: 0;
          margin-right: 120px;

          &::after {
            background-color: map.get(colors.$themeColors, color-interactive-8);
          }
        }
      }
    }
  }

  .ns-footer.ns-footer-storefront {
    .dist-info span {
      line-height: 22px;
      font-size: 16px;
      font-weight: 300;
    }

    .navigation-container::before {
      background-color: map.get(colors.$themeColors, color-interactive-9);
    }
  }
}

/* minimal-footer */
.ns-footer.ns-minimal-footer {
  .footer-top {
    background-color: rgba(0, 0, 0, 0);
    color: black;
    border-top: solid 1px black;
    border-bottom: solid 1px black;

    .nav-item-link-anchor {
      color: black;
    }

    .ns-footer-nav {
      color: black;
    }

    .logo-container {
      background-color: rgba(0, 0, 0, 0);

      .logo-container-button {
        a.join-button {
          background-color: black;
        }
      }
    }
  }

  .footer-bottom {
    display: flex;

    .seals-container {
      background-color: rgba(0, 0, 0, 0);
    }
  }

  .nav-item-link-anchor {
    color: black;
  }

  .navigation-container {
    border-right: solid 1px black;
  }

  .seals-container {
    background-color: rgba(0, 0, 0, 0);
  }

  .seals-container-full {
    display: none;
  }

  .seals-container-minimal {
    .max-crop {
      min-width: 715px;
    }
  }
}

@media (max-width: 1440px) {
  .ns-footer.ns-minimal-footer {
    .seals-container-full {
      display: block;
    }

    .max-crop {
      justify-content: center;
    }

    .seals-container-minimal {
      display: none;
    }

    .footer-bottom {
      justify-content: center;
    }

    .navigation-container {
      border-right: none;
    }
    .footer-top {
      .logo-container {
        .logo-container-button {
          margin-right: 25px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .ns-footer.ns-minimal-footer {
    .footer-top {
      .logo-container {
        .logo-container-button {
          margin-right: auto;
        }
      }
      .navigation-container {
        .ns-container {
          .social-icons {
            display: flex;
            justify-content: center;
          }
        }
      }
    }
  }
}
</style>
